/* eslint-disable sort-keys-fix/sort-keys-fix */
import { useEffect, useState } from 'react'

import { AxiosResponse } from 'axios'
import { Button, Row, Span } from 'components'
import { ExcelIcon, PdfIcon } from 'components/Svg'
import { colorsStatus } from 'components/Icons/StatusIcon/StatusIcon'
import { useAuth, useLoading, useModal, useReportPrint, useToggle } from 'hooks'
import {
  EffectiveTableData,
  EfetivoLogicoServices,
  ResGetAllContracts,
  EffectiveData,
  ReqUpdateStatusEffective,
  updateEffectiveResponse,
  ReqEffectiveByContract
} from 'services/Supplier/LogicalEffective'
import { COLORS, FORMAT, SPACING } from 'utils'

import { changeTabProps } from '../../../LogicalEffectiveRegister'
import EditEffectiveModal from '../EditEffectiveModal/EditEffectiveModal'
import { TableRegisterEffectives } from '../TableRegisterEffectives/TableRegisterEffectives'

// A interface agora aceita os dados iniciais de efetivos como props
interface CollapseProps {
  contractData: ResGetAllContracts & { effectiveList?: EffectiveTableData[], pageCount?: number }
  handleChangeTab: (props: changeTabProps) => void
  pesquisaGeral: string,
}

export const CollapseContent = ({
  contractData,
  handleChangeTab,
  pesquisaGeral
}: CollapseProps) => {
  const { setLoading } = useLoading()
  const { user } = useAuth()
  const { handlePrintEfetivoLogico } = useReportPrint()
  const { showModal } = useModal()
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [rowsPerPage, setRowsPerPage] = useState(15)
  const [firstLoad, setFirstLoad] = useState(true);
  // O estado local de pageCount agora é inicializado com a vinda da props
  const [pageCount, setPageCount] = useState(contractData.pageCount || 0)

  // O estado local de effectiveList agora é inicializado com a vinda da props
  const [effectiveList, setEffectiveList] = useState<EffectiveTableData[]>(contractData.effectiveList || [])

  const [selectedEffective, setSelectedEffective] = useState<
    EffectiveData | undefined
  >()
  const [openEditEffectiveModal, toggleEditEffectiveModal] = useToggle()
  const selectedContract = contractData.cdContrato
  const selectedNmEmpresa = contractData.nmFornecedor

  // Efeito para sincronizar o estado local com as props quando o pai atualizar os dados (ex: nova pesquisa)
  useEffect(() => {
    setEffectiveList(contractData.effectiveList || [])
    setPageCount(contractData.pageCount || 0)
    setPageNumber(1) // Reseta a página para a 1 ao receber novos dados
  }, [contractData.effectiveList, contractData.pageCount])


  // Esta função agora é usada APENAS para paginação. A carga inicial não a chama mais.
  const getEfetivosPorContrato = async () => {
    setLoading('loading')

    const requestObj: ReqEffectiveByContract = {
      cdContrato: selectedContract,
      idUsuario: user.IdUsuario,
      pageNumber: pageNumber,
      isContraparte: true,
      pageSize: rowsPerPage,
      pesquisaGeral
    }

    try {
      const response = await EfetivoLogicoServices().getEfetivosporContrato(
        requestObj
      )

      if (response.status !== 200) {
        throw new Error(
          'Página em construção, não há necessidade de lançamentos.'
        )
      }

      setEffectiveList(response.data.items)
      setPageCount(response.data.pageCount)
      setLoading('idle')
    } catch (error: any) {
      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message:
          error.mensagem ||
          error.message ||
          'Página em construção, não há necessidade de lançamentos.',
        txtButtonConfirm: 'Fechar',
        type: 'error'
      })
    }
  }

  // O useEffect agora só dispara para paginação e pesquisa, mas não mais na montagem do componente
  // pois os dados já chegam via props.
  // Adicionamos uma condição para não buscar a página 1 novamente, já que ela vem do pai.
  useEffect(() => {
    if (!firstLoad || pageNumber !== 1) {
      getEfetivosPorContrato();
    }
    setFirstLoad(false);
  }, [pageNumber, rowsPerPage, pesquisaGeral])


  const getEffectiveData = async (idEfetivoLogico: number) => {
    setLoading('loading')

    try {
      const response = await EfetivoLogicoServices().getEfetivoLogicoById(
        idEfetivoLogico
      )

      if (response.status !== 200) {
        throw new Error('Não foi possivel obter dados do Efetivo')
      }

      setSelectedEffective(response.data)
      setLoading('idle')
      showModal({
        titleModal: 'Atenção',
        message: 'Você deseja alterar este cadastro?',
        type: 'alert',
        txtButtonConfirm: 'Sim',
        handleConfirm() {
          toggleEditEffectiveModal()
        },
        txtButtonCancel: 'Cancelar'
      })
    } catch (error: any) {
      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message:
          error.mensagem ||
          error.message ||
          'Não foi possivel obter dados do Efetivo, tente novamente.',
        txtButtonConfirm: 'Fechar',
        type: 'error'
      })
    }
  }

  const revalidateCollaborator = async (idEfetivoLogico: number) => {
    setLoading('loading')

    try {
      const response = await EfetivoLogicoServices().revalidaEfetivoLogico(
        idEfetivoLogico
      )

      if (response.status !== 200) {
        throw new Error(
          'Não foi possível revalidar colaborador, tente novamente.'
        )
      }

      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message: 'Colaborador foi revalidado com sucesso!',
        txtButtonConfirm: 'Fechar',
        type: 'success'
      })

      getEfetivosPorContrato()
    } catch (error: any) {
      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message:
          error.mensagem ||
          error.message ||
          'Não foi possivel revalidar colaborador, tente novamente.',
        txtButtonConfirm: 'Fechar',
        type: 'error'
      })
    }
  }

  const handleClickRevalidate = (
    idEfetivoLogico: number,
    nmColaborador: string
  ) => {
    showModal({
      handleConfirm: () => {
        revalidateCollaborator(idEfetivoLogico)
      },
      message: `Revalidar acesso do colaborador ${nmColaborador} ?`,
      txtButtonConfirm: 'Revalidar',
      type: 'alert'
    })
  }

  const changeStatusCollaborator: (
    params: ReqUpdateStatusEffective
  ) => Promise<void> = async (params: ReqUpdateStatusEffective) => {
    const actionDone: 'liberado' | 'bloqueado' | 'demitido' =
      params.status === 'L' ? 'liberado' : params.status === 'D' ? 'demitido' : 'bloqueado'
    setLoading('loading')

    try {
      const reqUpdateStatusEffectiveObj: ReqUpdateStatusEffective = {
        cdContrato: params.cdContrato,
        flEnviaScarf: params.flEnviaScarf,
        flReplicarStatus: params.flReplicarStatus,
        cdCpf: params.cdCpf,
        idColaboradorEfetivoLogico: params.idColaboradorEfetivoLogico,
        idContrato: contractData.idContrato || 0,
        status: params.status
      }
      const response: AxiosResponse<updateEffectiveResponse> =
        await EfetivoLogicoServices().atualizaStatusColaborador(
          reqUpdateStatusEffectiveObj
        )

      if (response.status !== 200) {
        throw new Error(`Colaborador não foi ${actionDone}, verifique!`)
      }

      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message: `Colaborador ${actionDone} com sucesso!`,
        txtButtonConfirm: 'Fechar',
        type: 'success'
      })

      params.flReplicarStatus &&
        handleChangeTab({
          contractData: contractData,
          newValue: 0,
          resetTab: false
        })
      getEfetivosPorContrato()
    } catch (error: any) {
      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message: `${error.response.data.title}. Não ${actionDone}, verifique!`,
        txtButtonConfirm: 'Fechar',
        type: 'error'
      })
    }
  }

  const handleClickBlock = (
    idEfetivoLogico: number,
    nmColaborador: string,
    maisDeUmContrato: boolean,
    cdCpf: string
  ) => {
    const updateStatusToBlockEffectiveObj: ReqUpdateStatusEffective = {
      flEnviaScarf: false,
      idColaboradorEfetivoLogico: idEfetivoLogico,
      cdCpf: cdCpf,
      cdContrato: selectedContract,
      status: 'D'
    }

    const showSecondModal = (updateStatusToBlockEffectiveObj: ReqUpdateStatusEffective) => {
      showModal({
        hideThirdButton: true,
        message: `Você tem certeza que deseja demitir esse colaborador? Esse processo não poderá ser revertido.`,
        handleConfirm: () => {
          const newUpdateStatusObject: ReqUpdateStatusEffective = {
            ...updateStatusToBlockEffectiveObj,
            flReplicarStatus: false
          }
          changeStatusCollaborator(newUpdateStatusObject)
        },
        handleCancel: () => {
          setTimeout(() => handleBack(updateStatusToBlockEffectiveObj), 0);
        },
        txtButtonCancel: 'Não',
        typeButtonCancel: 'outlined',
        txtButtonConfirm: 'Sim',
        type: 'alert'
      });
    };

    const handleBack = (updateStatusToBlockEffectiveObj: ReqUpdateStatusEffective) => {
      showModal({
        hideThirdButton: false,
        message: `Você deseja bloquear ou demitir este colaborador?`,
        handleConfirm: () => {
          setTimeout(() => showSecondModal(updateStatusToBlockEffectiveObj), 0);
        },
        handleCancel: () => {
          const newUpdateStatusObject: ReqUpdateStatusEffective = {
            ...updateStatusToBlockEffectiveObj,
            flReplicarStatus: false,
            status: 'B'
          }
          changeStatusCollaborator(newUpdateStatusObject)
        },
        txtButtonCancel: 'Bloquear',
        typeButtonCancel: 'outlined',
        txtButtonConfirm: 'Demitir',
        type: 'alert'
      });
    };

    showModal({
      hideThirdButton: false,
      message: `Você deseja bloquear ou demitir este colaborador?`,
      handleConfirm: () => {
        setTimeout(() => showSecondModal(updateStatusToBlockEffectiveObj), 0);
      },
      handleCancel: () => {
        const newUpdateStatusObject: ReqUpdateStatusEffective = {
          ...updateStatusToBlockEffectiveObj,
          flReplicarStatus: false,
          status: 'B'
        }
        changeStatusCollaborator(newUpdateStatusObject)
      },
      txtButtonCancel: 'Bloquear',
      typeButtonCancel: 'outlined',
      txtButtonConfirm: 'Demitir',
      type: 'alert'
    });
  }

  const handleClickUnlock = (
    idEfetivoLogico: number,
    nmColaborador: string,
    maisDeUmContrato: boolean,
    cdCpf: string
  ) => {
    showModal({
      handleConfirm: () => {
        const updateStatusToReleaseEffectiveObj: ReqUpdateStatusEffective = {
          flEnviaScarf: false,
          idColaboradorEfetivoLogico: idEfetivoLogico,
          cdCpf: cdCpf,
          cdContrato: selectedContract,
          status: 'L'
        }
        changeStatusCollaborator(updateStatusToReleaseEffectiveObj)
      },
      message: `Você deseja desbloquear o colaborador ${nmColaborador}?`,
      txtButtonConfirm: 'Desbloquear',
      type: 'alert'
    })
  }

  const updateEffectiveData = async (effectiveData: EffectiveData) => {
    setLoading('loading')

    const requestQuery: EffectiveData = {
      cdCep: FORMAT.unMask(effectiveData.cdCep),
      cdCnh: FORMAT.unMask(effectiveData.cdCnh),
      cdCpf: FORMAT.unMask(effectiveData.cdCpf),
      cdCtps: FORMAT.unMask(effectiveData.cdCtps),
      cdDocumento: FORMAT.unMask(effectiveData.cdDocumento),
      cdPis: FORMAT.unMask(effectiveData.cdPis),
      cdSerieCtps: effectiveData.cdSerieCtps,
      dtNascimento: new Date(effectiveData.dtNascimento),
      flUsaRefeitorio: effectiveData.usaRefeitorio,
      idContrato: contractData.idContrato,
      idEfetivoLogico: effectiveData.idEfetivoLogico,
      nmColaborador: effectiveData.nmColaborador,
      nuNumero: effectiveData.nuNumero,
      sgEstadoCtps: effectiveData.sgEstadoCtps,
      sgEstadoDocto: effectiveData.sgEstadoDocto,
      sgEstadoEnd: effectiveData.sgEstadoEnd,
      sgGenero: effectiveData.sgGenero,
      txBairro: effectiveData.txBairro,
      txCidade: effectiveData.txCidade,
      txComplemento: effectiveData.txComplemento,
      txEmissorDocto: effectiveData.txEmissorDocto,
      txEndereco: effectiveData.txEndereco,
      txFiliacaoMae: effectiveData.txFiliacaoMae,
      txFiliacaoPai: effectiveData.txFiliacaoPai,
      txFuncao: effectiveData.txFuncao,
      txLocalAcesso: effectiveData.txLocalAcesso,
      txMatricula: effectiveData.txMatricula,
      txNacionalidade: effectiveData.txNacionalidade,
      txNaturalidade: effectiveData.txNaturalidade
    }

    try {
      const response = await EfetivoLogicoServices().updateEfetivoLogico(
        requestQuery
      )

      if (response.status !== 200) {
        throw new Error('Erro ao salvar dados do Colaborador, tente novamente')
      }

      showModal({
        handleConfirm: () => {
          toggleEditEffectiveModal()
          handleChangeTab({
            contractData: contractData,
            newValue: 0,
            resetTab: false
          })
        },
        hideButtonCancel: true,
        message: 'Colaborador atualizado com sucesso!',
        txtButtonConfirm: 'Fechar',
        type: 'success'
      })

      setLoading('idle')
    } catch {
      showModal({
        hideButtonCancel: true,
        message: 'Erro ao salvar dados do Colaborador, tente novamente',
        txtButtonConfirm: 'Fechar',
        type: 'error'
      })
      setLoading('idle')
    }
  }

  const handleClickAddEffective = () => {
    handleChangeTab({
      contractData: contractData,
      newValue: 1
    })
  }

  const handleExcel = async () => {
    if (effectiveList.length > 0) {
      setLoading('loading')

      try {
        // 1. Busca os efetivos para conferir se tem dados (similar ao relatório PDF)
        const requestCheckData: ReqEffectiveByContract = {
          cdContrato: selectedContract,
          idUsuario: user.IdUsuario,
          isContraparte: true,
          flObterTodos: true, // Pega todos os registros para conferir
          pageNumber: 1,
          pageSize: 9999
        }

        const checkResponse = await EfetivoLogicoServices().getEfetivosporContrato(requestCheckData)

        if (checkResponse.status !== 200 || checkResponse.data.items.length === 0) {
          setLoading('idle')
          showModal({
            hideButtonCancel: true,
            message: 'Não há efetivo cadastrado para este contrato.',
            txtButtonConfirm: 'Fechar',
            type: 'alert'
          })
          return
        }

        // 2. Se tem dados, agora chama o serviço para pegar o arquivo Excel
        const response = await EfetivoLogicoServices().getExcelEfetivoLogico({
          cdContrato: selectedContract,
          idUsuario: user.IdUsuario,
          isContraparte: true
        })

        if (response.status !== 200) {
          throw new Error('Erro ao baixar arquivo Excel, tente novamente.')
        }

        const url =
          'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,' +
          response.data
        const downloadLink = document.createElement('a')
        downloadLink.href = url
        downloadLink.download = `efetivo-logico-contrato-${selectedContract}.xlsx`
        document.body.appendChild(downloadLink)
        downloadLink.click()
        document.body.removeChild(downloadLink)

        setLoading('idle')
      } catch {
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: 'Erro ao baixar arquivo Excel, tente novamente.',
          txtButtonConfirm: 'Fechar',
          type: 'error'
        })
      }
    } else {
      showModal({
        hideButtonCancel: true,
        message: 'Não há efetivo cadastrado para este contrato.',
        txtButtonConfirm: 'Fechar',
        type: 'alert'
      })
    }
  }


  const getStatusColor = (cdStatus: string) => {
    const statusName = { B: 'Bloqueado', L: 'Liberado', P: 'Pendente' }
    return {
      content: statusName[cdStatus] || 'Pendente',
      styles: {
        fillColor: colorsStatus[cdStatus],
        fontStyle: 'bold'
      }
    }
  }

  const handleClickPdfReport = async () => {
    const requestForReport: ReqEffectiveByContract = {
      cdContrato: selectedContract,
      isContraparte: true,
      flObterTodos: true,
      idUsuario: user.IdUsuario,
      pageNumber: 1,
      pageSize: 9999
    }

    setLoading('loading')

    try {
      const response = await EfetivoLogicoServices().getEfetivosporContrato(
        requestForReport
      )

      if (response.status !== 200) {
        throw new Error(
          'Página em construção, não há necessidade de lançamentos.'
        )
      }

      setLoading('idle')

      if (response.data.items.length > 0) {
        handlePrintEfetivoLogico({
          filename: `relatorio-efetivo-logico-contrato-${selectedContract}`,
          data: {
            titleCollapse: contractData.objetoContrato,
            cdContrato: selectedContract,
            empresa: selectedNmEmpresa,
            dataIni: contractData.dtInicioVigencia,
            dataFim: contractData.dtFimVigencia,
            body: response.data.items?.map((efetivo) => [
              efetivo.nmColaborador,
              FORMAT.documento(efetivo.cdCpf),
              FORMAT.formatDDMMYYYY(efetivo.dtNascimento),
              efetivo.txFuncao,
              FORMAT.formatDDMMYYYY(efetivo.dtUltAtualizacao),
              efetivo.qtDias,
              getStatusColor(efetivo.statusBloqueio)
            ])
          }
        })
      } else {
        showModal({
          hideButtonCancel: true,
          message: 'Não há efetivo cadastrado para este contrato.',
          txtButtonConfirm: 'Fechar',
          type: 'alert'
        })
      }
    } catch (error: any) {
      setLoading('idle')
      showModal({
        hideButtonCancel: true,
        message:
          error.mensagem ||
          error.message ||
          'Página em construção, não há necessidade de lançamentos.',
        txtButtonConfirm: 'Fechar',
        type: 'error'
      })
    }
  }

  const handleDeleteEfetivoContrato = (idEfetivoLogicoContrato: number) => {
    showModal({
      type: 'alert',
      titleModal: 'Atenção!',
      message: 'Tem certeza de que deseja deletar este efetivo lógico?',
      txtButtonConfirm: 'SIM',
      txtButtonCancel: 'Cancelar',
      handleConfirm: () => {
        setLoading('loading')

        EfetivoLogicoServices()
          .deleteEfetivoLogicoByContrato(idEfetivoLogicoContrato)
          .then(({ status }) => {
            if (status === 200) {
              getEfetivosPorContrato()

              showModal({
                type: 'success',
                message: 'Efetivo lógico excluido com sucesso',
                txtButtonConfirm: 'OK',
                hideButtonCancel: true
              })
            }
          })
          .catch(() => {
            showModal({
              type: 'error',
              message: 'Não foi possivel atualizar o efetivo lógico',
              txtButtonConfirm: 'OK',
              hideButtonCancel: true
            })
          })
          .finally(() => setLoading('idle'))
      }
    })
  }

  return (
    <>
      <Row
        justifyContent="flex-end"
        marginTop={SPACING.spacing10}
        marginBottom={SPACING.spacing10}
      >
        <Button
          text="ADICIONAR NOVO"
          onClick={() => handleClickAddEffective()}
        />
      </Row>
      {effectiveList.length === 0 ? (
        <Row
          alignItems="center"
          justifyContent="center"
          marginBottom={SPACING.spacing30}
          marginTop={SPACING.spacing20}
        >
          <Span
            text="Não há colaborador cadastrado para este contrato."
            fontSize="1rem"
            fontWeight="bold"
            color={COLORS.info}
          />
        </Row>
      ) : (
        <TableRegisterEffectives
          effectiveList={effectiveList}
          handleDeleteEfetivoContrato={handleDeleteEfetivoContrato}
          setPage={setPageNumber}
          pageCount={pageCount}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
          page={pageNumber}
          handleClickRevalidate={handleClickRevalidate}
          handleClickBlock={handleClickBlock}
          handleClickUnlock={handleClickUnlock}
          handleEditEffective={getEffectiveData}
        />
      )}
      <Row
        justifyContent="flex-start"
        marginTop={SPACING.spacing10}
        marginBottom={SPACING.spacing10}
      >
        <ExcelIcon
          onClick={() => handleExcel()}
          disabled={effectiveList.length === 0}
        />
        <PdfIcon
          onClick={() => handleClickPdfReport()}
          disabled={effectiveList.length === 0}
        />
      </Row>
      <EditEffectiveModal
        showModal={openEditEffectiveModal}
        toggleModal={toggleEditEffectiveModal}
        effectiveData={selectedEffective}
        handleSubmitUpdate={updateEffectiveData}
        contractData={contractData}
      />
    </>
  )
}